import { useGetAutoCompleteStoreList } from '@/hooks/location';
import { useDebounce } from '@/hooks/debounce';
import useDeviceDetect from '@/hooks/deviceDetect';
import { IArea, IAutoCompleteStore, IOption, IStoreLists } from '@/interfaces/store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BasicInput } from '@/components/BasicInput';
import { LocationSelectors } from '@/components/Location/LocationSelectors';
import { StaggerItem } from '@/components/Location/StaggerItem';
import StoreCardSkeleton from '@/components/Location/StoreCardSkeleton';
import Map from '@/components/Map';
import StoreDetails from '@/components/StoreDetails';

interface BopisStorePickerProps {
  districts: IOption[];
  thanas: IOption[];
  districtId: string;
  thanaId: string;
  onDistrictChange: (value: string) => void;
  onThanaChange: (value: string) => void;

  selectedStore: IStoreLists;
  userCurrentLatLong?: { latitude?: number; longitude?: number };

  setSelectedStore: (s: IStoreLists) => void;
  setMyLocation: (a: IArea) => void;

  setShowLocationModal?: (b: boolean) => void;
  showLocationModal?: boolean;

  /**
   * Mobile auto-confirm path. On mobile (per `useDeviceDetect`) the user
   * has no Confirm button, so picking a store must commit the draft and
   * close the modal in one step. Desktop ignores this — Confirm stays manual.
   */
  onCommitAndClose?: () => void;

  /**
   * Whether the modal is in BOPIS mode (vs. Express). Gates the store-
   * autocomplete query so it only fires when the modal is visible AND
   * BOPIS is the active mode.
   */
  isBopis?: boolean;

  storeList?: IStoreLists[];
  isStoreListLoading?: boolean;

  findStore: (data: any) => void;
  storeSearchValue: string;
  onStoreSearchValueChange: (value: string) => void;

  Loader: React.ComponentType;

  // INVARIANT: bopisPickedLocation uses Map-shape {longitude, latitude} end-to-end.
  bopisPickedLocation: { longitude: number; latitude: number } | null;
  setBopisPickedLocation: (loc: { longitude: number; latitude: number } | null) => void;
}

const MIN_VIEWPORT_HEIGHT = 587;
const HEADER_FOOTER_HEIGHT = 240;

export const BopisStorePicker = ({
  districts,
  thanas,
  districtId,
  thanaId,
  onDistrictChange,
  onThanaChange,
  selectedStore,
  userCurrentLatLong,
  setSelectedStore,
  setMyLocation,
  setShowLocationModal,
  showLocationModal,
  onCommitAndClose,
  isBopis = false,
  storeList,
  isStoreListLoading = false,
  findStore,
  storeSearchValue,
  Loader,
  bopisPickedLocation,
  setBopisPickedLocation,
}: BopisStorePickerProps) => {
  const { isMobile } = useDeviceDetect();

  // Local draft for the search input.
  const [searchDraft, setSearchDraft] = useState<string>(storeSearchValue ?? '');

  // Keep the draft in sync if the parent resets the committed value externally
  // (modal reset, X close), otherwise a cleared storeSearchValue would leave
  // the input visually showing the stale draft.
  useEffect(() => {
    setSearchDraft(storeSearchValue ?? '');
  }, [storeSearchValue]);

  const debounceQuery = useDebounce(searchDraft, 300);
  const isPickerVisible = !!(showLocationModal && isBopis);
  const { data: autoCompleteStoreList, isLoading: isAutoCompleteLoading } = useGetAutoCompleteStoreList(debounceQuery, isPickerVisible);

  const isSearchPending = searchDraft.length > 0 && debounceQuery !== searchDraft;
  const isListLoading = isStoreListLoading || isAutoCompleteLoading || isSearchPending;

  // useMemo (not useState+useEffect) so the filtered list updates on the same
  // render that storeList changes.
  const filteredStoreList = useMemo<IStoreLists[]>(() => {
    const suggestions = autoCompleteStoreList?.Suggestions;

    const searchInFlight = isAutoCompleteLoading || (searchDraft.length > 0 && debounceQuery !== searchDraft);

    if (searchInFlight) {
      return storeList ?? [];
    }

    if (storeList && suggestions && suggestions.length > 0) {
      const autoCompleteIds = new Set(suggestions.map((store: IAutoCompleteStore) => store.Id));
      return storeList.filter((store: IStoreLists) => autoCompleteIds.has(store.Id));
    }

    if (storeList && (!suggestions || suggestions.length === 0) && searchDraft.length > 0) {
      return [];
    }

    return storeList ?? [];
  }, [storeList, autoCompleteStoreList, searchDraft, isAutoCompleteLoading, debounceQuery]);

  const handleSetSelectedLocation = useCallback(
    (loc: IStoreLists) => {
      setSelectedStore(loc);
      const lng = Number(loc.Longitude);
      const lat = Number(loc.Latitude);

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setBopisPickedLocation({ longitude: lng, latitude: lat });
      }

      // Marker click on mobile auto-confirms (no Confirm button shown there);
      // desktop keeps the Confirm button as the manual path.
      if (isMobile) {
        onCommitAndClose?.();
      }
    },
    [setSelectedStore, setBopisPickedLocation, onCommitAndClose, isMobile],
  );

  // Hydrate the BOPIS pin from the persisted store on modal open so a
  // previously-selected store shows up on the map without forcing a re-click.
  const lastSyncedStoreIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!showLocationModal) {
      lastSyncedStoreIdRef.current = undefined;
      return;
    }

    if (!selectedStore?.Id) {
      lastSyncedStoreIdRef.current = undefined;
      return;
    }

    if (lastSyncedStoreIdRef.current === selectedStore.Id) {
      return;
    }

    lastSyncedStoreIdRef.current = selectedStore.Id;
    const lng = Number(selectedStore.Longitude);
    const lat = Number(selectedStore.Latitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    setBopisPickedLocation({ longitude: lng, latitude: lat });
  }, [showLocationModal, selectedStore, setBopisPickedLocation]);

  const initialCenter = useMemo(() => {
    if (bopisPickedLocation) {
      return {
        longitude: bopisPickedLocation.longitude,
        latitude: bopisPickedLocation.latitude,
        zoom: 13,
      };
    }

    if (selectedStore?.Id && Number.isFinite(Number(selectedStore.Latitude)) && Number.isFinite(Number(selectedStore.Longitude))) {
      return {
        longitude: Number(selectedStore.Longitude),
        latitude: Number(selectedStore.Latitude),
        zoom: 13,
      };
    }

    if (userCurrentLatLong?.longitude !== undefined && userCurrentLatLong?.latitude !== undefined) {
      return {
        longitude: userCurrentLatLong.longitude as number,
        latitude: userCurrentLatLong.latitude as number,
        zoom: 12,
      };
    }

    return undefined;
  }, [bopisPickedLocation, selectedStore, userCurrentLatLong]);

  return (
    <div className="bopis-picker-grid mb-5 grid h-[667px] grid-cols-1 gap-4 overflow-hidden sm:h-[760px] md:h-[500px] md:grid-cols-2">
      <div className="bopis-list-col flex h-[341px] min-h-0 flex-col sm:h-[300px] md:h-full" onWheel={(e) => e.stopPropagation()}>
        <StaggerItem show={!!showLocationModal} delayMs={0}>
          <LocationSelectors
            districts={districts}
            thanas={thanas}
            districtId={districtId}
            thanaId={thanaId}
            onDistrictChange={onDistrictChange}
            onThanaChange={onThanaChange}
          />
        </StaggerItem>

        <StaggerItem show={!!showLocationModal} delayMs={60} className="mb-3 flex items-stretch gap-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              findStore({
                store: [searchDraft ?? ''],
              });
            }}
            className="flex w-full items-stretch gap-2"
          >
            <div className="relative flex-1">
              <BasicInput
                placeholder="Search Store"
                onChange={(event: any) => setSearchDraft(event.target.value)}
                value={searchDraft}
                name="store"
                className="flex-1"
              />
            </div>
          </form>
        </StaggerItem>

        <StaggerItem
          show={!!showLocationModal}
          delayMs={120}
          className="bopis-list-scroll relative min-h-0 flex-1 overflow-y-auto border border-solid border-[#C8C8C8]"
        >
          <div className="bopis-store-details h-full w-full" onWheel={(e) => e.stopPropagation()} aria-busy={isListLoading}>
            <StoreDetails
              isEditable={false}
              isBOPIS={true}
              isLoading={isListLoading}
              store={isListLoading ? [] : filteredStoreList}
              userCurrentLatLong={userCurrentLatLong}
              setSelectedStore={setSelectedStore}
              selectedStore={selectedStore}
              setShowLocationModal={setShowLocationModal}
              showLocationModal={showLocationModal}
              setMyLocation={setMyLocation}
              closeOnSelect={false}
            />
            {isListLoading && (
              <div className="absolute inset-0 z-10 bg-white">
                <StoreCardSkeleton />
              </div>
            )}
          </div>
        </StaggerItem>
      </div>

      <StaggerItem show={!!showLocationModal} delayMs={200} className="bopis-map-col relative h-[300px] w-full md:h-[500px]">
        <Map
          locations={filteredStoreList}
          selectedLocation={selectedStore?.Id ? [selectedStore] : []}
          setSelectedLocation={handleSetSelectedLocation}
          initialCenter={initialCenter}
          pickedLocation={bopisPickedLocation}
          hidePickedMarker
        />
      </StaggerItem>
      <style jsx global>{`
        .bopis-store-details .border-solid.border-black {
          border-color: transparent !important;
          border-width: 0 !important;
        }
        @media (min-width: 768px) and (min-height: ${MIN_VIEWPORT_HEIGHT}px) {
          .bopis-picker-grid,
          .bopis-list-col,
          .bopis-map-col,
          .bopis-list-scroll {
            max-height: min(calc(100vh - ${HEADER_FOOTER_HEIGHT}px), 500px);
          }
        }
        /* iPad-portrait (768×1024) and similar 768–1023px widths. The side-by-side desktop layout resumes at ≥1024px. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .bopis-picker-grid {
            grid-template-columns: 1fr !important;
            height: 700px !important;
            max-height: 700px !important;
          }
          .bopis-list-col {
            height: 400px !important;
            max-height: 400px !important;
          }
          .bopis-map-col {
            height: 290px !important;
            max-height: 290px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BopisStorePicker;
